import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Loader2, Mail, MailCheck, AlertCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient, authErrorMessage, appUrl, MIN_PASSWORD_LENGTH } from "@/lib/auth-client";

function AuthCard({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex flex-col min-h-[100dvh] items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <Link href="/" className="flex items-center justify-center gap-2 font-bold text-xl text-foreground mb-6" data-testid="link-home">
          <Mail className="h-6 w-6 text-primary" />
          <span>MailCraft</span>
        </Link>
        <div className="rounded-xl border border-border bg-card shadow-sm p-6">
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          <div className="mt-5">{children}</div>
        </div>
        {footer && <div className="text-sm text-muted-foreground text-center mt-4 space-y-1">{footer}</div>}
      </div>
    </div>
  );
}

function InlineError({ children, testId = "text-auth-error" }: { children: ReactNode; testId?: string }) {
  return (
    <div className="flex gap-2 items-start rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert" data-testid={testId}>
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 items-start rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground" role="status" data-testid="text-auth-notice">
      <MailCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
      <div>{children}</div>
    </div>
  );
}

function Field(props: { id: string; label: string; type?: string; value: string; onChange: (v: string) => void; autoComplete?: string; hint?: string; extra?: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={props.id}>{props.label}</Label>
        {props.extra}
      </div>
      <Input
        id={props.id}
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        required
        data-testid={`input-${props.id}`}
      />
      {props.hint && <p className="text-xs text-muted-foreground">{props.hint}</p>}
    </div>
  );
}

function useTitle(t: string) {
  useEffect(() => { document.title = `${t} | MailCraft`; }, [t]);
}

/** Sends (or re-sends) the verification email. */
function ResendVerification({ email }: { email: string }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [msg, setMsg] = useState("");
  async function resend() {
    if (!email) return;
    setState("sending");
    const { error } = await authClient.sendVerificationEmail({ email, callbackURL: appUrl("/sign-in?verified=1") });
    if (error) {
      setState("error");
      setMsg(authErrorMessage(error));
    } else {
      setState("sent");
    }
  }
  return (
    <div className="mt-2 text-sm">
      {state === "sent" ? (
        <span className="text-muted-foreground" data-testid="text-verification-resent">Verification email sent to {email}.</span>
      ) : (
        <button type="button" onClick={resend} disabled={state === "sending" || !email} className="text-primary font-medium hover:underline disabled:opacity-60" data-testid="button-resend-verification">
          {state === "sending" ? "Sending..." : "Resend verification email"}
        </button>
      )}
      {state === "error" && <div className="text-destructive mt-1">{msg}</div>}
    </div>
  );
}

export function SignInPage() {
  useTitle("Sign In");
  const search = new URLSearchParams(useSearch());
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(search.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<{ text: string; unverified: boolean } | null>(null);
  const verified = search.get("verified") === "1";
  const reset = search.get("reset") === "1";
  const urlError = search.get("error");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const { error: err } = await authClient.signIn.email({ email: email.trim(), password });
    setPending(false);
    if (err) {
      setError({ text: authErrorMessage(err), unverified: err.code === "EMAIL_NOT_VERIFIED" || err.status === 403 });
      return;
    }
    queryClient.clear();
    setLocation("/app");
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Welcome back to MailCraft."
      footer={
        <>
          <div>New here, or had a MailCraft account before? <Link href="/sign-up" className="text-primary font-medium hover:underline" data-testid="link-sign-up">Activate your account</Link></div>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {verified && !error && <Notice>Email verified. Sign in to continue.</Notice>}
        {reset && !error && <Notice>Password updated. Sign in with your new password.</Notice>}
        {urlError && !error && <InlineError>{authErrorMessage({ code: urlError.toUpperCase() }, "That link could not be used. Request a new one.")}</InlineError>}
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <Field
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          extra={<Link href={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`} className="text-xs text-primary hover:underline" data-testid="link-forgot-password">Forgot password?</Link>}
        />
        {error && (
          <InlineError>
            {error.text}
            {error.unverified && <ResendVerification email={email.trim()} />}
          </InlineError>
        )}
        <Button type="submit" className="w-full" disabled={pending} data-testid="button-sign-in">
          {pending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Sign in
        </Button>
      </form>
    </AuthCard>
  );
}

export function SignUpPage() {
  useTitle("Activate Account");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) return setError(`Passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    if (password !== confirm) return setError("Passwords do not match.");
    setPending(true);
    const { error: err } = await authClient.signUp.email({
      email: email.trim(),
      name: name.trim(),
      password,
      callbackURL: appUrl("/sign-in?verified=1"),
    });
    setPending(false);
    if (err) return setError(authErrorMessage(err));
    setSentTo(email.trim());
  }

  if (sentTo) {
    return (
      <AuthCard
        title="Check your email"
        subtitle={<>We sent a verification link to <span className="font-medium text-foreground" data-testid="text-sent-to">{sentTo}</span>.</>}
        footer={<Link href="/sign-in" className="text-primary font-medium hover:underline" data-testid="link-sign-in">Back to sign in</Link>}
      >
        <p className="text-sm text-muted-foreground">Open the link to verify your address, then sign in. If you used MailCraft before, your templates, logs and settings will be waiting.</p>
        <ResendVerification email={sentTo} />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Activate your account"
      subtitle="Create a password for MailCraft. Already had an account? Use the same email to keep all your data."
      footer={<div>Already activated? <Link href="/sign-in" className="text-primary font-medium hover:underline" data-testid="link-sign-in">Sign in</Link></div>}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field id="name" label="Full name" value={name} onChange={setName} autoComplete="name" />
        <Field id="email" label="Work email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <Field id="password" label="Password" type="password" value={password} onChange={setPassword} autoComplete="new-password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`} />
        <Field id="confirm-password" label="Confirm password" type="password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
        {error && <InlineError>{error}</InlineError>}
        <Button type="submit" className="w-full" disabled={pending} data-testid="button-sign-up">
          {pending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Continue
        </Button>
      </form>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  useTitle("Forgot Password");
  const search = new URLSearchParams(useSearch());
  const [email, setEmail] = useState(search.get("email") ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const { error: err } = await authClient.requestPasswordReset({ email: email.trim(), redirectTo: appUrl("/reset-password") });
    setPending(false);
    if (err) return setError(authErrorMessage(err));
    setSent(true);
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="Enter your email and we will send you a link to choose a new password."
      footer={<Link href="/sign-in" className="text-primary font-medium hover:underline" data-testid="link-sign-in">Back to sign in</Link>}
    >
      {sent ? (
        <Notice>If an account exists for {email.trim()}, a reset link is on its way. It expires shortly, so use it soon.</Notice>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
          {error && <InlineError>{error}</InlineError>}
          <Button type="submit" className="w-full" disabled={pending} data-testid="button-send-reset">
            {pending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Send reset link
          </Button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  useTitle("Choose Password");
  const search = new URLSearchParams(useSearch());
  const [, setLocation] = useLocation();
  const token = search.get("token");
  const urlError = search.get("error");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token || urlError) {
    return (
      <AuthCard
        title="Link expired"
        footer={<Link href="/sign-in" className="text-primary font-medium hover:underline" data-testid="link-sign-in">Back to sign in</Link>}
      >
        <InlineError>This password reset link is invalid or has expired.</InlineError>
        <Link href="/forgot-password" className="block mt-4" data-testid="link-request-new-reset">
          <span className="inline-flex w-full items-center justify-center rounded-md bg-primary text-primary-foreground h-9 text-sm font-medium">Request a new link</span>
        </Link>
      </AuthCard>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) return setError(`Passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    if (password !== confirm) return setError("Passwords do not match.");
    setPending(true);
    const { error: err } = await authClient.resetPassword({ newPassword: password, token: token! });
    setPending(false);
    if (err) return setError(authErrorMessage(err));
    setLocation("/sign-in?reset=1");
  }

  return (
    <AuthCard
      title="Choose a new password"
      footer={<Link href="/sign-in" className="text-primary font-medium hover:underline" data-testid="link-sign-in">Back to sign in</Link>}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field id="password" label="New password" type="password" value={password} onChange={setPassword} autoComplete="new-password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`} />
        <Field id="confirm-password" label="Confirm password" type="password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
        {error && <InlineError>{error}</InlineError>}
        <Button type="submit" className="w-full" disabled={pending} data-testid="button-reset-password">
          {pending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Save password
        </Button>
      </form>
    </AuthCard>
  );
}

export function AuthLoading() {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-background" data-testid="status-auth-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
